import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import { useAuth }    from '@/providers/AuthProvider';
import { useToast }   from '@/providers/ToastProvider';
import { getErrorMessage } from '@/api/client';
import {
  PageHeader, Button, Input, Badge, Spinner, EmptyState, ConfirmDialog, Card,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { Plus, Pencil, Trash2, Eye, Search } from 'lucide-react';

export default function RecipesListPage() {
  const { t }       = useTranslation();
  const { isAdmin } = useAuth();
  const { toast }   = useToast();
  const qc          = useQueryClient();

  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['recipes', { page, search }],
    queryFn:  () => recipesApi.list({ page, limit, search: search || undefined }),
  });

  const deleteMutation = useMutation({
    mutationFn: recipesApi.delete,
    onSuccess: () => {
      toast(t('recipes.deleted'));
      qc.invalidateQueries({ queryKey: ['recipes'] });
      setDeleteId(null);
    },
    onError: (err) => toast(getErrorMessage(err), 'error'),
  });

  return (
    <div>
      <PageHeader title={t('recipes.title')}>
        {isAdmin && (
          <Button asChild size="sm">
            <Link to="/recipes/new"><Plus size={16} /> {t('recipes.new')}</Link>
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder={t('common.search')} className="pl-9" />
        </div>
      </div>

      {/* Desktop */}
      <Card className="hidden md:block">
        {isLoading ? <Spinner className="py-16" /> : data?.data.length === 0 ? (
          <EmptyState message={t('common.noResults')}>
            {isAdmin && <Button asChild size="sm" variant="outline"><Link to="/recipes/new"><Plus size={14} /> {t('recipes.new')}</Link></Button>}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('recipes.name')}</TableHead>
                <TableHead>{t('recipes.type')}</TableHead>
                <TableHead className="text-right">{t('recipes.portionWeight')}</TableHead>
                <TableHead className="text-right">{t('recipes.sellingPrice')}</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.recipe_type_name ?? '—'}</TableCell>
                  <TableCell className="text-right">{r.portion_weight_g} g</TableCell>
                  <TableCell className="text-right">{r.selling_price ? `$${Number(r.selling_price).toFixed(2)}` : '—'}</TableCell>
                  <TableCell>
                    <Badge variant={r.is_active ? 'success' : 'secondary'}>
                      {r.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-28">
                    <div className="flex gap-1">
                      <Button asChild variant="ghost" size="icon"><Link to={`/recipes/${r.id}`}><Eye size={14} /></Link></Button>
                      {isAdmin && <>
                        <Button asChild variant="ghost" size="icon"><Link to={`/recipes/${r.id}/edit`}><Pencil size={14} /></Link></Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteId(r.id)}><Trash2 size={14} /></Button>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile */}
      <div className="md:hidden space-y-3">
        {isLoading ? <Spinner className="py-12" /> : (data?.data ?? []).map(r => (
          <Card key={r.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.recipe_type_name ?? '—'}</p>
              </div>
              <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? t('common.active') : t('common.inactive')}</Badge>
            </div>
            <div className="mt-3 flex gap-2">
              <Button asChild variant="outline" size="sm" className="flex-1"><Link to={`/recipes/${r.id}`}><Eye size={13} /> View</Link></Button>
              {isAdmin && <>
                <Button asChild variant="outline" size="sm"><Link to={`/recipes/${r.id}/edit`}><Pencil size={13} /></Link></Button>
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDeleteId(r.id)}><Trash2 size={13} /></Button>
              </>}
            </div>
          </Card>
        ))}
      </div>

      {data && data.meta.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, data.meta.total)} of {data.meta.total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= data.meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        message={t('recipes.deleteConfirm')}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
